import { AppointmentActionKey } from '@/features/maintenance-data/appointmentMessages';
import { RowSelectionDerived } from '@/features/maintenance-data/hooks/useRowSelection';

export type ActionKey = 'markSolved' | 'revertSolved' | AppointmentActionKey;

const APPOINTMENT_ACTIONS: ReadonlySet<ActionKey> = new Set<ActionKey>([
    'schedule',
    'editAppointment',
    'cancelAppointment',
]);

/**
 * Pure Actions-dropdown state machine (MANSC-27550).
 *
 * Resolves the *base* set of action keys from the local selection flags, then applies the WFE
 * `availableActions` gate: appointment actions (`schedule`/`editAppointment`/`cancelAppointment`)
 * are only kept if also advertised by WFE. `markSolved`/`revertSolved` are local-only and never
 * gated.
 *
 * | Selection | Vehicles | Solved | Appointment | Base actions |
 * |-----------|----------|--------|-------------|--------------|
 * | none      | —        | —      | —           | []           |
 * | 1+        | same     | none   | none        | markSolved, schedule |
 * | 1+        | same     | none   | all same    | editAppointment, cancelAppointment |
 * | 1+        | any      | all    | —           | revertSolved |
 * | 1+        | same     | mixed  | —           | [] (disabled) |
 * | 1+        | multiple | !all   | —           | [] (disabled) |
 */
export const resolveActionKeys = (
    selection: RowSelectionDerived,
    availableActions?: ReadonlySet<AppointmentActionKey>,
): ActionKey[] => {
    const { selectedCount, selectedVehicleCount, allSolved, noneSolved, allHaveSameAppointment, noneHaveAppointment } =
        selection;

    const base = resolveBaseActionKeys({
        selectedCount,
        selectedVehicleCount,
        allSolved,
        noneSolved,
        allHaveSameAppointment,
        noneHaveAppointment,
    });

    // When no gate is supplied, all appointment actions are allowed (standalone 27550 behavior).
    if (!availableActions) {
        return base;
    }
    return base.filter((key) => !APPOINTMENT_ACTIONS.has(key) || availableActions.has(key as AppointmentActionKey));
};

const resolveBaseActionKeys = (selection: {
    selectedCount: number;
    selectedVehicleCount: number;
    allSolved: boolean;
    noneSolved: boolean;
    allHaveSameAppointment: boolean;
    noneHaveAppointment: boolean;
}): ActionKey[] => {
    const { selectedCount, selectedVehicleCount, allSolved, noneSolved, allHaveSameAppointment, noneHaveAppointment } =
        selection;

    if (selectedCount === 0) {
        return [];
    }

    // All solved (any number of vehicles) → revert only.
    if (allSolved) {
        return ['revertSolved'];
    }

    // Appointment-capable states require a single vehicle and none solved.
    if (selectedVehicleCount === 1 && noneSolved) {
        if (noneHaveAppointment) {
            return ['markSolved', 'schedule'];
        }
        if (allHaveSameAppointment) {
            return ['editAppointment', 'cancelAppointment'];
        }
    }

    // Mixed solved/unsolved, multi-vehicle not-all-solved, or inconsistent appointments → disabled.
    return [];
};
